const { debounce, getElement } = window.BAO.Utils

/* eslint-disable no-console */
class Cache {
  constructor () {
    /** @type {Map<String, String>} */
    this.cache = new Map()
  }

  exists (key) {
    return this.cache.has(key)
  }

  /**
   * @param {String} key
   * @param {String} value
   */
  set (key, value) {
    this.cache.set(key, value)
  }

  /**
   * @param {String} key
   */
  get (key) {
    return this.cache.get(key)
  }

  delete (key) {
    this.cache.delete(key)
  }
}

class DataFetcher {
  constructor () {
    this.cache = new Cache()
  }

  /**
   * @param {String} url
   * @param {String} section
   */
  async fetch (url, section) {
    return this.cache.exists(url)
      ? this.cache.get(url)
      : await this.#fetchData(url, section)
  }

  /**
   * @param {String} url
   * @param {String} section
   *
   * @returns {Promise<String>}
   */
  async #fetchData (url, section) {
    const html = await window.BAO.sectionRenderer.fetchSingle(section, url)

    this.cache.set(url, html)

    return html
  }
}

class ShoeFinder extends window.BAO.CustomElement() {
  fetcher = new DataFetcher()

  connectedCallback () {
    super.connectedCallback()

    this.initialLoad = false

    this.questionsEl = this.els.questions?.element
    this.launcherContainerEl = this.els.launcherContainer?.element
    this.questionsContainerEl = this.els.questionsContainer?.element
    this.productsContainerEl = this.els.productsContainer?.element

    this.searchParamsFromStorage = window.localStorage.getItem(
      this.searchParamsStorageName,
    )

    this.collectionControlsEl = this.externalEls?.collectionControls?.element

    if (this.collectionControlsEl && this.searchParamsFromStorage !== null) {
      this.collectionControlsEl.removeAttribute('aria-hidden')
    }

    if (this.searchParamsFromStorage !== null) {
      this.renderQuestions()

      this.activeSection = 'questions'
    }
  }

  setupListeners () {
    super.setupListeners()

    // Button listeners
    if (this.els.launchButton?.exists) {
      this.listeners.add(
        this.els.launchButton.element,
        `click`,
        this.handleLaunchClick.bind(this),
      )
    }

    if (this.els.restartButton?.exists) {
      this.listeners.add(
        this.els.restartButton.element,
        `click`,
        this.handleRestartClick.bind(this),
      )
    }

    if (this.els.resultsButton?.exists) {
      this.listeners.add(
        this.els.resultsButton.element,
        `click`,
        this.handleResultsClick.bind(this),
      )
    }

    // Form listeners
    this.formEl = this.els.form?.element
    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmit(event)
    }, 500)
    this.listeners.add(this.formEl, 'input', this.debouncedOnSubmit.bind(this))
    this.listeners.add(this.formEl, 'submit', this.onFormSubmit.bind(this))

    // Window resize
    this.onResize = this.onResize.bind(this)
    this.debouncedResize = debounce((event) => {
      this.onResize(event)
    }, 240)
    this.listeners.add(window, 'resize', this.debouncedResize)
  }

  onSubmit (event) {
    event.preventDefault()

    if (!event.target.closest(this.els.input.selector)) return

    this.questionsLoading = true

    const parentForm = event.target.closest(this.els.form?.selector)

    parentForm.dispatchEvent(
      new Event('submit', {
        bubbles: true,
        cancelable: true,
      }),
    )
  }

  onFormSubmit (event) {
    event.preventDefault()

    const formData = new FormData(event.target)
    const searchParams = new URLSearchParams(formData).toString()

    this.renderQuestions(searchParams, event)
  }

  onResize () {
    this.storeFooterHeight()
  }

  async handleLaunchClick () {
    this.restart()

    await this.minDurationLoading(this.renderQuestions.bind(this))

    this.activeSection = 'questions'
  }

  async handleRestartClick (event, loading = true) {
    if (loading === true) {
      await this.minDurationLoading(this.restart.bind(this))
    } else {
      this.restart()
    }

    this.activeSection = 'launcher'
  }

  handleResultsClick (event) {
    event.preventDefault()

    const href = this.els.resultsButton?.element?.href

    if (href) {
      this.loading = true

      setTimeout(() => {
        window.location.href = href
      }, this.minLoadingDuration)
    }
  }

  async renderQuestions (searchParams = null, event = null) {
    if (searchParams === null && this.searchParamsFromStorage != null) {
      searchParams = this.searchParamsFromStorage
    } else if (searchParams !== null) {
      window.localStorage.setItem(this.searchParamsStorageName, searchParams)
      window.localStorage.setItem(this.searchParamsStorageName, searchParams)
    }

    const sectionHtml = await this.fetchSection(searchParams)
    const sectionDOM = new DOMParser().parseFromString(
      sectionHtml,
      'text/html',
    )
    const resultsCount = sectionDOM.querySelector('[data-results-count]')
      ?.dataset.resultsCount
    const singleProductResultsHref = sectionDOM.querySelector(
      '[data-single-product-results-href]',
    )?.dataset.singleProductResultsHref

    if (this.initialLoad === false) {
      this.formEl.innerHTML = sectionDOM.querySelector(
        this.els.form.selector,
      )?.innerHTML

      this.initialLoad = true
    }

    this.productsContainerEl.innerHTML = sectionDOM.querySelector(
      this.els.productsContainer.selector,
    )?.innerHTML

    if (this.questionsEl && resultsCount) {
      this.questionsEl.setAttribute('data-results-filtered', resultsCount)
    }

    if (searchParams) {
      this.updateInputStates(sectionDOM, event)
      this.updateResultsButton(
        searchParams,
        resultsCount,
        singleProductResultsHref,
      )
    }

    this.questionsLoading = false

    requestAnimationFrame(() => {
      this.storeFooterHeight()
    })
  }

  updateInputStates (sectionDOM, event) {
    const currentInputs = this.els.input?.elements

    if (!currentInputs) return

    const inputThatSubmitted = event?.submitter?.closest(
      this.els.input.selector,
    )

    const inputsToUpdate = inputThatSubmitted
      ? currentInputs.filter((input) => input !== inputThatSubmitted)
      : currentInputs

    inputsToUpdate.forEach((input) => {
      const selector = `${this.els.input.selector}[name="${input.name}"][value="${input.value}"]`
      const newInput = sectionDOM.querySelector(selector)

      if (newInput) {
        input.checked = newInput.checked
        input.disabled = newInput.disabled
      }
    })
  }

  resetInputStates () {
    const inputs = this.els.input?.elements

    if (!inputs) return

    inputs.forEach((input) => {
      input.checked = false
      input.disabled = false
    })
  }

  updateResultsButton (searchParams, resultsCount, singleProductResultsHref) {
    const resultsButton = this.els.resultsButton?.element
    const footer = this.externalEls.footer?.element

    if (resultsButton) {
      resultsButton.href =
        singleProductResultsHref || `${this.collectionUrl}?${searchParams}`
    }

    if (footer) {
      if (Number(resultsCount) <= this.showResultsButtonThreshold) {
        footer.setAttribute('data-show-submit', true)
      } else {
        footer.removeAttribute('data-show-submit')
      }
    }
  }

  restart () {
    this.els.launchButton?.element.focus()

    this.searchParamsFromStorage = null

    window.localStorage.removeItem(this.searchParamsStorageName)
    window.localStorage.removeItem(this.carouselPositionStorageName)

    this.resetInputStates()

    this.externalEls.footer?.element?.removeAttribute('data-show-submit')

    const carouselEl = getElement(this.carouselSelector)
    if (carouselEl) carouselEl.moveToIdx(0)
  }

  async fetchSection (searchParams = null) {
    const url = `${this.collectionUrl}?section_id=${this.sectionType}&${searchParams}`
    const html = await this.fetcher.fetch(url, this.sectionId)
    return html
  }

  updateActivePanel (visiblePanel) {
    const panels = {
      launcher: this.launcherContainerEl,
      questions: this.questionsContainerEl,
    }

    for (const [key, el] of Object.entries(panels)) {
      el.setAttribute('aria-hidden', String(key !== visiblePanel))
    }
  }

  async minDurationLoading (fn) {
    this.loading = true

    const start = Date.now()

    // Wait for animation to play in
    await new Promise((resolve) =>
      setTimeout(resolve, this.loadingInitialDelay),
    )

    try {
      await fn()
    } finally {
      const elapsed = Date.now() - start
      const remaining = this.minLoadingDuration - elapsed

      if (remaining > 0) {
        await new Promise((resolve) => setTimeout(resolve, remaining))
      }

      this.loading = false
    }
  }

  storeFooterHeight () {
    this.style.setProperty(
      '--ShoeFinder_FooterHeight',
      `${this.footerHeight}px`,
    )
  }

  set activeSection (section) {
    this._activeSection = section

    const sections = {
      launcher: this.launcherContainerEl,
      questions: this.questionsContainerEl,
    }

    for (const [key, el] of Object.entries(sections)) {
      el.setAttribute('aria-hidden', String(key !== section))

      if (key === section) {
        el.focus()
      }
    }

    if (section === 'questions') this.storeFooterHeight()
  }

  get footerHeight () {
    const footer = this.externalEls.footer?.element
    return footer ? footer.offsetHeight : 0
  }

  get activeSection () {
    return this._activeSection || 'launcher'
  }

  set loading (val) {
    this.setAttribute('aria-busy', val.toString())
  }

  get loading () {
    return this.getAttribute('aria-busy') === 'true'
  }

  set questionsLoading (val) {
    return this.questionsEl?.setAttribute('aria-busy', val.toString())
  }

  get questionsLoading () {
    return this.questionsEl?.getAttribute('aria-busy') === 'true'
  }

  get minLoadingDuration () {
    return 1000
  }

  get loadingInitialDelay () {
    return 500
  }

  get showResultsButtonThreshold () {
    return 4
  }

  get sectionType () {
    return `shoe-finder-questions`
  }

  get sectionId () {
    return `shoe-finder-questions`
  }

  get carouselSelector () {
    return `shoe-finder-carousel`
  }

  get searchParamsStorageName () {
    return `BAO-shoe-finder-search-params`
  }

  get carouselPositionStorageName () {
    return `BAO-shoe-finder-carousel-position`
  }

  get collectionUrl () {
    return this.dataset.collectionUrl
  }

  get shoeFinderDocumentTitle () {
    return this.dataset.documentTitle || 'P448 Shoe Finder'
  }
}

if (!customElements.get('shoe-finder')) {
  customElements.define('shoe-finder', ShoeFinder)
}
